import { useEffect, useState } from "react";
import axios from "axios";
import { API, resolveImg } from "@/App";

const PIN_KEY = "tid_admin_pin";

function useAdminPin() {
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_KEY) || "");
  const save = (v) => { setPin(v); localStorage.setItem(PIN_KEY, v); };
  const clear = () => { setPin(""); localStorage.removeItem(PIN_KEY); };
  return [pin, save, clear];
}

function adminAxios(pin) {
  return axios.create({ baseURL: API, headers: { "X-Admin-Pin": pin } });
}

export default function Admin() {
  const [pin, setPin, clearPin] = useAdminPin();
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [pinInput, setPinInput] = useState(pin);
  const [tab, setTab] = useState("guests");

  useEffect(() => {
    if (!pin) return;
    axios.post(`${API}/admin/verify`, { pin })
      .then(() => setVerified(true))
      .catch(() => { setVerified(false); clearPin(); });
  }, [pin]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/admin/verify`, { pin: pinInput });
      setPin(pinInput);
      setVerified(true);
    } catch (err) {
      setError("PIN incorrecto");
    }
  };

  if (!verified) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <span className="eyebrow">Acceso privado</span>
        <h1 className="font-serif text-5xl mt-3">Panel admin.</h1>
        <p className="prose-bio mt-4 text-[14px]">
          Introduce el PIN para gestionar invitados, miembros y páginas de la tertulia.
        </p>
        <form onSubmit={login} className="mt-8 space-y-4">
          <input
            type="password"
            placeholder="PIN de administrador"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            className="w-full bg-transparent border-b border-[var(--line)] py-3 text-[15px] focus:outline-none focus:border-[var(--gold)]"
            data-testid="admin-pin-input"
            autoFocus
          />
          {error && <p className="text-red-600 text-[12px]">{error}</p>}
          <button type="submit" className="btn-ghost w-full justify-center" data-testid="admin-login-btn">
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="admin-panel">
      <div className="flex justify-between items-center flex-wrap gap-4 mb-8">
        <div>
          <span className="eyebrow">Panel privado</span>
          <h1 className="font-serif text-5xl leading-none mt-2">Administración.</h1>
        </div>
        <button onClick={() => { clearPin(); setVerified(false); }} className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)] hover:text-[var(--text-ink)]" data-testid="admin-logout">
          Cerrar sesión
        </button>
      </div>

      <div className="flex gap-2 border-b border-[var(--line)] mb-10 overflow-x-auto">
        {["guests", "members", "upcoming", "seed"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-3 text-[12px] uppercase tracking-[0.22em] border-b-2 transition-colors whitespace-nowrap ${tab === t ? "border-[var(--text-ink)] text-[var(--text-ink)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-ink)]"}`}
            data-testid={`admin-tab-${t}`}
          >
            {t === "guests" ? "Invitados" : t === "members" ? "Miembros" : t === "upcoming" ? "Próximas tertulias" : "Base de datos"}
          </button>
        ))}
      </div>

      {tab === "guests" && <GuestsAdmin pin={pin} />}
      {tab === "members" && <MembersAdmin pin={pin} />}
      {tab === "upcoming" && <UpcomingAdmin pin={pin} />}
      {tab === "seed" && <SeedAdmin pin={pin} />}
    </div>
  );
}

// ============ GUESTS ADMIN ============
function GuestsAdmin({ pin }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // slug being edited, or "new"
  const api = adminAxios(pin);

  const load = () => axios.get(`${API}/guests`).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (slug) => {
    if (!confirm("¿Eliminar este invitado definitivamente?")) return;
    await api.delete(`/guests/${slug}`);
    load();
  };

  return (
    <div data-testid="guests-admin">
      <div className="flex justify-between items-center mb-6">
        <p className="text-[13px] text-[var(--text-muted)]">{list.length} invitados en el archivo.</p>
        <button onClick={() => setEditing("new")} className="btn-ghost" data-testid="new-guest-btn">+ Nuevo invitado</button>
      </div>

      {editing && (
        <GuestForm
          pin={pin}
          slug={editing === "new" ? null : editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((g) => (
          <div key={g.id} className="glass p-4 flex gap-4" data-testid={`admin-guest-${g.slug}`}>
            <img src={resolveImg(g.image)} alt={g.name} className="w-16 h-20 object-cover" />
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg leading-tight truncate">{g.name}</h3>
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-[var(--gold-deep)] mt-1">
                {g.year || "—"} · {g.featured_media ? "En medios" : "Sin medios"}
              </p>
              <p className="text-[11px] text-[var(--text-muted)] mt-1 line-clamp-2">{g.role}</p>
              <div className="flex gap-2 mt-2 text-[10.5px] uppercase tracking-[0.18em]">
                <button onClick={() => setEditing(g.slug)} className="border-b border-[var(--gold)]">Editar</button>
                <button onClick={() => onDelete(g.slug)} className="border-b border-red-400 text-red-600">Eliminar</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuestForm({ pin, slug, onClose }) {
  const api = adminAxios(pin);
  const [form, setForm] = useState({
    name: "", role: "", visit_date: "", image: "", bio_html: "",
    featured_home: true, featured_media: false, media_image: "", media_subtitle: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (slug) {
      axios.get(`${API}/guests/${slug}`).then((r) => setForm({ ...form, ...r.data }));
    }
  }, [slug]);

  const upload = async (e, field) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const r = await api.post("/upload", fd);
    setForm((v) => ({ ...v, [field]: r.data.url }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        name: form.name, role: form.role, visit_date: form.visit_date || null,
        image: form.image, bio_html: form.bio_html,
        featured_home: form.featured_home, featured_media: form.featured_media,
        media_image: form.media_image || null, media_subtitle: form.media_subtitle || null,
      };
      if (slug) await api.put(`/guests/${slug}`, payload);
      else await api.post("/guests", payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-start justify-center py-10 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[var(--bg-master)] max-w-3xl w-full p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="guest-form">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-3xl">{slug ? "Editar invitado" : "Nuevo invitado"}</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-ink)] text-2xl leading-none">×</button>
        </div>
        <form onSubmit={save} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Nombre completo *">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" data-testid="guest-name" />
            </Field>
            <Field label="Profesión / cargo">
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" data-testid="guest-role" />
            </Field>
          </div>
          <Field label="Fecha de visita (ej: 15 de octubre de 2016)">
            <input value={form.visit_date || ""} onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
              className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" data-testid="guest-date" />
          </Field>

          <Field label="Foto del invitado">
            <div className="flex items-center gap-4">
              {form.image && <img src={resolveImg(form.image)} className="w-20 h-24 object-cover border border-[var(--line)]" />}
              <input type="file" accept="image/*" onChange={(e) => upload(e, "image")} data-testid="guest-image-upload" />
              <input placeholder="o pega URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="flex-1 bg-transparent border-b border-[var(--line)] py-2 text-[12px] focus:outline-none" />
            </div>
          </Field>

          <Field label="Biografía (HTML permitido: <p> <strong> <em> <a>)">
            <textarea rows={8} value={form.bio_html} onChange={(e) => setForm({ ...form, bio_html: e.target.value })}
              className="w-full bg-transparent border border-[var(--line)] p-3 font-mono text-[12px] focus:outline-none focus:border-[var(--gold)]" data-testid="guest-bio" />
          </Field>

          <div className="border-t border-[var(--line)] pt-5">
            <label className="flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] mb-4">
              <input type="checkbox" checked={form.featured_media} onChange={(e) => setForm({ ...form, featured_media: e.target.checked })} />
              Mostrar en "En los medios"
            </label>
            {form.featured_media && (
              <div className="space-y-4 pl-6 border-l-2 border-[var(--gold)]">
                <Field label="Foto del recorte de prensa">
                  <div className="flex items-center gap-4">
                    {form.media_image && <img src={resolveImg(form.media_image)} className="w-28 h-20 object-cover border border-[var(--line)]" />}
                    <input type="file" accept="image/*" onChange={(e) => upload(e, "media_image")} data-testid="guest-media-upload" />
                    <input placeholder="o pega URL" value={form.media_image || ""} onChange={(e) => setForm({ ...form, media_image: e.target.value })}
                      className="flex-1 bg-transparent border-b border-[var(--line)] py-2 text-[12px] focus:outline-none" />
                  </div>
                </Field>
                <Field label="Pie de foto / medio (ABC, Diario de Sevilla…)">
                  <input value={form.media_subtitle || ""} onChange={(e) => setForm({ ...form, media_subtitle: e.target.value })}
                    className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" />
                </Field>
              </div>
            )}
          </div>

          {error && <p className="text-red-600 text-[12px]">{error}</p>}
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="btn-ghost" style={{borderColor:"var(--text-muted)",color:"var(--text-muted)"}}>Cancelar</button>
            <button type="submit" disabled={saving} className="btn-ghost" data-testid="guest-save">
              {saving ? "Guardando…" : slug ? "Guardar cambios" : "Crear invitado"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ MEMBERS ADMIN ============
function MembersAdmin({ pin }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const api = adminAxios(pin);

  const load = () => axios.get(`${API}/members`).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (slug) => {
    if (!confirm("¿Dar de baja a este miembro definitivamente? (puedes también desactivarlo editándolo)")) return;
    await api.delete(`/members/${slug}`);
    load();
  };

  return (
    <div data-testid="members-admin">
      <div className="flex justify-between items-center mb-6">
        <p className="text-[13px] text-[var(--text-muted)]">{list.length} miembros.</p>
        <button onClick={() => setEditing("new")} className="btn-ghost" data-testid="new-member-btn">+ Nuevo miembro</button>
      </div>

      {editing && (
        <MemberForm pin={pin} slug={editing === "new" ? null : editing}
          onClose={() => { setEditing(null); load(); }} />
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((m) => (
          <div key={m.id} className="glass p-4 flex gap-4" data-testid={`admin-member-${m.slug}`}>
            <img src={resolveImg(m.image)} alt={m.name} className="w-16 h-20 object-cover" />
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg leading-tight truncate">{m.name}</h3>
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-[var(--gold-deep)] mt-1">
                {m.category} · {m.year_joined} {!m.active && "· Inactivo"}
              </p>
              <p className="text-[11px] text-[var(--text-muted)] mt-1 line-clamp-2">{m.role}</p>
              <div className="flex gap-2 mt-2 text-[10.5px] uppercase tracking-[0.18em]">
                <button onClick={() => setEditing(m.slug)} className="border-b border-[var(--gold)]">Editar</button>
                <button onClick={() => onDelete(m.slug)} className="border-b border-red-400 text-red-600">Baja</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberForm({ pin, slug, onClose }) {
  const api = adminAxios(pin);
  const [form, setForm] = useState({
    name: "", role: "", category: "General", year_joined: 2014, image: "", active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (slug) axios.get(`${API}/members`).then((r) => {
      const m = r.data.find((x) => x.slug === slug);
      if (m) setForm(m);
    });
  }, [slug]);

  const upload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const r = await api.post("/upload", fd);
    setForm((v) => ({ ...v, image: r.data.url }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        name: form.name, role: form.role, category: form.category,
        year_joined: Number(form.year_joined), image: form.image, active: form.active
      };
      if (slug) await api.put(`/members/${slug}`, payload);
      else await api.post("/members", payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const categories = ["Derecho", "Ciencia y Tecnología", "Economía y Empresa", "Humanidades y Cultura", "General"];

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-start justify-center py-10 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[var(--bg-master)] max-w-2xl w-full p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="member-form">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-3xl">{slug ? "Editar miembro" : "Nuevo miembro"}</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] text-2xl leading-none">×</button>
        </div>
        <form onSubmit={save} className="space-y-5">
          <Field label="Nombre completo *">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" data-testid="member-name" />
          </Field>
          <Field label="Profesión">
            <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" data-testid="member-role" />
          </Field>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Categoría">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Año de incorporación">
              <input type="number" min="2014" max="2030" value={form.year_joined} onChange={(e) => setForm({ ...form, year_joined: e.target.value })}
                className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" />
            </Field>
          </div>
          <Field label="Foto de perfil">
            <div className="flex items-center gap-4">
              {form.image && <img src={resolveImg(form.image)} className="w-20 h-24 object-cover border border-[var(--line)]" />}
              <input type="file" accept="image/*" onChange={upload} data-testid="member-image-upload" />
              <input placeholder="o pega URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="flex-1 bg-transparent border-b border-[var(--line)] py-2 text-[12px] focus:outline-none" />
            </div>
          </Field>
          <label className="flex items-center gap-2 text-[12px] uppercase tracking-[0.2em]">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Miembro activo (desmarcar equivale a baja)
          </label>
          {error && <p className="text-red-600 text-[12px]">{error}</p>}
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="btn-ghost" style={{borderColor:"var(--text-muted)",color:"var(--text-muted)"}}>Cancelar</button>
            <button type="submit" disabled={saving} className="btn-ghost" data-testid="member-save">
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ UPCOMING ADMIN ============
function UpcomingAdmin({ pin }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const api = adminAxios(pin);

  const load = () => axios.get(`${API}/upcoming`).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!confirm("¿Eliminar esta tertulia programada?")) return;
    await api.delete(`/upcoming/${id}`);
    load();
  };

  return (
    <div data-testid="upcoming-admin">
      <div className="flex justify-between items-center mb-6">
        <p className="text-[13px] text-[var(--text-muted)]">{list.length} tertulia{list.length !== 1 ? "s" : ""} programada{list.length !== 1 ? "s" : ""}.</p>
        <button onClick={() => setEditing("new")} className="btn-ghost" data-testid="new-upcoming-btn">+ Nueva tertulia</button>
      </div>

      {editing && (
        <UpcomingForm pin={pin} item={editing === "new" ? null : list.find((x) => x.id === editing)}
          onClose={() => { setEditing(null); load(); }} />
      )}

      {list.length === 0 ? (
        <p className="text-[var(--text-muted)] italic py-10">No hay próximas tertulias. Añade la primera.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {list.map((u) => (
            <div key={u.id} className="glass p-5" data-testid={`admin-upcoming-${u.id}`}>
              <div className="flex justify-between items-start gap-3 mb-3">
                <div>
                  <span className="eyebrow">{u.date} {u.time}</span>
                  <h3 className="font-serif text-2xl leading-tight mt-1">{u.guest_name}</h3>
                  {u.guest_role && <p className="text-[12px] text-[var(--text-muted)] italic">{u.guest_role}</p>}
                </div>
                {u.image && <img src={resolveImg(u.image)} alt="" className="w-16 h-20 object-cover" />}
              </div>
              {u.location && <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)]">📍 {u.location}</p>}
              {u.description && <p className="text-[12px] text-[var(--text-muted)] mt-2 line-clamp-2">{u.description}</p>}
              <div className="flex gap-3 mt-4 text-[10.5px] uppercase tracking-[0.18em]">
                <button onClick={() => setEditing(u.id)} className="border-b border-[var(--gold)]">Editar</button>
                <button onClick={() => onDelete(u.id)} className="border-b border-red-400 text-red-600">Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UpcomingForm({ pin, item, onClose }) {
  const api = adminAxios(pin);
  const [form, setForm] = useState(item || {
    guest_name: "", guest_role: "", date: "", time: "", location: "Sevilla",
    description: "", image: "", status: "confirmed"
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const upload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const r = await api.post("/upload", fd);
    setForm((v) => ({ ...v, image: r.data.url }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        guest_name: form.guest_name, guest_role: form.guest_role,
        date: form.date, time: form.time, location: form.location,
        description: form.description, image: form.image, status: form.status,
      };
      if (item) await api.put(`/upcoming/${item.id}`, payload);
      else await api.post("/upcoming", payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-start justify-center py-10 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[var(--bg-master)] max-w-2xl w-full p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="upcoming-form">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-3xl">{item ? "Editar tertulia" : "Nueva tertulia"}</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] text-2xl leading-none">×</button>
        </div>
        <form onSubmit={save} className="space-y-5">
          <Field label="Nombre del invitado *">
            <input required value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
              className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" data-testid="up-guest-name" />
          </Field>
          <Field label="Cargo / profesión">
            <input value={form.guest_role} onChange={(e) => setForm({ ...form, guest_role: e.target.value })}
              className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" />
          </Field>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Fecha (AAAA-MM-DD) *">
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" data-testid="up-date" />
            </Field>
            <Field label="Hora (ej: 20:30)">
              <input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" />
            </Field>
          </div>
          <Field label="Lugar">
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]" />
          </Field>
          <Field label="Tema / descripción">
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-transparent border border-[var(--line)] p-3 text-[13px] focus:outline-none focus:border-[var(--gold)]" />
          </Field>
          <Field label="Foto del invitado (opcional)">
            <div className="flex items-center gap-4">
              {form.image && <img src={resolveImg(form.image)} className="w-20 h-24 object-cover border border-[var(--line)]" />}
              <input type="file" accept="image/*" onChange={upload} />
              <input placeholder="o URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="flex-1 bg-transparent border-b border-[var(--line)] py-2 text-[12px] focus:outline-none" />
            </div>
          </Field>
          <Field label="Estado">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="bg-transparent border-b border-[var(--line)] py-2 focus:outline-none focus:border-[var(--gold)]">
              <option value="confirmed">Confirmada</option>
              <option value="tentative">Por confirmar</option>
            </select>
          </Field>
          {error && <p className="text-red-600 text-[12px]">{error}</p>}
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="btn-ghost" style={{borderColor:"var(--text-muted)",color:"var(--text-muted)"}}>Cancelar</button>
            <button type="submit" disabled={saving} className="btn-ghost" data-testid="upcoming-save">
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ SEED ADMIN ============
function SeedAdmin({ pin }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const run = async () => {
    if (!window.confirm("Esto reemplaza TODOS los datos por el seed inicial del archivo WordPress. ¿Continuar?")) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/seed?force=true`, {}, { headers: { "X-Admin-Pin": pin } });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Error" });
    } finally { setLoading(false); }
  };
  const downloadBackup = () => {
    setDownloading(true);
    // Open the endpoint in a new tab with pin as query param — browser handles the download
    const url = `${API}/admin/backup?pin=${encodeURIComponent(pin)}`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => setDownloading(false), 2000);
  };
  return (
    <div className="max-w-xl space-y-12">
      <div>
        <h2 className="font-serif text-3xl mb-3">Copia de seguridad</h2>
        <p className="prose-bio text-[14px] mb-6">
          Descarga un <strong>archivo ZIP</strong> con toda tu base de datos (<code>database.db</code>) y
          todas las fotos subidas (<code>uploads/</code>). Guárdalo en un lugar seguro — te permitirá
          restaurar el sitio en cualquier servidor.
        </p>
        <button
          onClick={downloadBackup}
          disabled={downloading}
          className="btn-ghost"
          data-testid="backup-btn"
        >
          {downloading ? "Generando ZIP…" : "↓ Descargar copia de seguridad (.zip)"}
        </button>
      </div>

      <div className="hairline"></div>

      <div>
        <h2 className="font-serif text-3xl mb-3">Restaurar seed</h2>
        <p className="prose-bio text-[14px] mb-6">
          Restaura todos los invitados y miembros desde el archivo de importación original de WordPress.
          <strong className="block mt-2 text-[var(--gold-deep)]">⚠️ Reemplaza todos los cambios realizados.</strong>
        </p>
        <button onClick={run} disabled={loading} className="btn-ghost" data-testid="reseed-btn">
          {loading ? "Restaurando…" : "Restaurar datos iniciales"}
        </button>
        {result && (
          <pre className="mt-6 p-4 bg-[rgba(18,18,18,0.04)] text-[12px] border border-[var(--line)]">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10.5px] uppercase tracking-[0.2em] text-[var(--text-muted)] block mb-2">{label}</label>
      {children}
    </div>
  );
}
